import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { PolicyAction, PolicyFormValues } from '../policy-dialog';
import z from 'zod';

import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

interface SuspendInactiveUsersActionProps {
  setValue: UseFormSetValue<PolicyFormValues>;
  control: Control<PolicyFormValues>;
  watch: UseFormWatch<PolicyFormValues>;
  errors: FieldErrors<PolicyFormValues>;
}

export const SuspendInactiveUsersActionSchema = z.object({
  type: z.literal('Suspend_Inactive_Users'),
  daysBack: z
    .number()
    .nullable()
    .refine((value) => value !== null, {
      message: 'A number is required.',
    }),
});

export type SuspendInactiveUsersActionType = z.infer<
  typeof SuspendInactiveUsersActionSchema
>;

// Type guard to check if an action is SuspendInactiveUsersActionType
function isSuspendInactiveUsersAction(
  action: PolicyAction
): action is SuspendInactiveUsersActionType {
  return action.type === 'Suspend_Inactive_Users';
}

export default function SuspendInactiveUsersAction({
  setValue,
  watch,
  control,
  errors,
}: SuspendInactiveUsersActionProps) {
  const actionData = watch('actionData');
  const idx = actionData
    .map((action) => action.type)
    .indexOf('Suspend_Inactive_Users');
  const actionAtIndex = actionData[idx];
  if (!isSuspendInactiveUsersAction(actionAtIndex)) {
    return null;
  }

  const currentAction: SuspendInactiveUsersActionType = actionAtIndex;
  const error: FieldErrors<SuspendInactiveUsersActionType> | undefined =
    errors?.actionData?.[idx];

  return (
    <Stack sx={{ p: 1 }}>
      <Stack>
        <Controller
          name={`actionData.${idx}.daysBack`}
          control={control}
          render={() => (
            <TextField
              inputProps={{
                id: 'daysBack',
                min: 0,
              }}
              InputLabelProps={{
                htmlFor: 'daysBack',
              }}
              type="number"
              label="Days since last log in:"
              value={currentAction.daysBack}
              onChange={(event) => {
                const value =
                  event.target.value === ''
                    ? null
                    : parseInt(event.target.value);
                setValue(`actionData.${idx}.daysBack`, value, {
                  shouldValidate: true,
                });
              }}
              sx={{ width: 250 }}
              error={!!error?.daysBack}
              helperText={error?.daysBack?.message || ' '}
              autoComplete="off"
            />
          )}
        />
      </Stack>
    </Stack>
  );
}
